/**
 * GrantBonusDialog.tsx — 发放 BONUS 弹窗（注册名 grant-bonus，ADM-FIN-S004）
 *
 * 「确认发放」走 POST /api/v1/finance/grant-bonus（financeApi.grantBonus + useMutation），body 严格
 * 按 shared GrantBonusRequest：{ user_id(UUID), amount(正整数 string ≤ 2000), reason_i18n }。
 * reason_i18n：locale→文案（1-500 字），**必含非空 `'en'` 键**（en = server 派生失败的 fallback 地板）。
 * 发放原因给 C 端展示 → MultiLangFormRows 多语言录入（必填 zh/en）；快捷原因 chips 预填中文行。
 *
 * 发放原因展示语言不再由运营手选：reason 展示语言由 server 按用户最近语言（last_seen_language_per_app）在
 * **展示侧**（C 端 /me/transactions/bonus）派生；grant 写侧只存 reason_i18n 池 + 校 en 必填（dev-docs
 * A1 §9 / #167，PR #321 merged）。故本弹窗移除原「目标语言手选 + locale 候选派生 + 候选空阻断」整套；
 * 用户体系 per App per user、user_id 不跨 App，无需手选 locale / 跨 App 语言仲裁。
 *
 * 成功按服务端真值 toast（amount / expires_at）+ invalidate ['users']；失败错误 toast 不关弹窗。
 * 「备注」契约无字段 → disabled + NOT-in-scope 标注。
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useSysConfig } from '~/data/queries';
import type { UserRow } from '~/data/queries';
import { financeApi } from '~/hooks/use-finance';
import { fmtDate } from '~/lib/format';
import { Icons } from '~/lib/icons';
import { Dialog } from '~/ui/Dialog';
import { type FormRowProps, FormRow as UiFormRow } from '~/ui/FormRow';
import { MultiLangFormRows, type MultiLangValue } from '~/ui/MultiLangFormRows';
import { toast } from '~/ui/Toast';

// design dialogs.jsx 的薄包装：固定 dialogs 几何，与原型 1:1
const FormRow = (p: FormRowProps) => (
  <UiFormRow labelWidth={140} gap={14} pad="12px 0" labelPadTop={6} {...p} />
);

const PRESETS = [100, 500, 1000, 2000];
// 快捷原因（中文）：点击预填 reason_i18n.zh；默认原因「用户补偿」对齐 ADM-FIN-S004
const REASONS = ['用户补偿', '客服补偿', '活动奖励', '故障补偿', 'VIP 关怀'];

const NOT_IN_SCOPE_TITLE =
  '待契约（NOT-in-scope，见 docs/plans/2026-06-12-web-real-api-integration-plan.md §五）';

export function GrantBonusDialog({ user, onClose }: { user?: UserRow; onClose: () => void }) {
  const u = user || { id: 'usr_unknown', name: '用户', bonus: 0, appId: '' };
  const { data: sysConfig } = useSysConfig();

  const [amount, setAmount] = useState(500);
  const [reasonI18n, setReasonI18n] = useState<MultiLangValue>({ zh: REASONS[0] ?? '', en: '' });

  // 必填只有 'en' —— 契约地板（`02-API契约/A1-运营财务.md` §9.1：reason_i18n 需 ≥1 键且
  // 必含非空 'en'，否则 FINANCE_BONUS_REASON_LOCALE_REQUIRED）。zh 仍固定展示并预填快捷原因，
  // 但**不强制**：发放时 server 按该 user 的 last_seen_language 从池里锁单语快照，而语言 Tab 候选集
  // 明文排除 zh（`01-通用约定/85-运营配置维度模型.md` §1.1）——zh 值永远不会被选成展示快照，
  // 强制它只是拦住「只填英文」这种完全合法的发放。
  const requiredLangs = ['en'];
  const pinnedLangs = ['zh', 'en'];
  // 「其他语言」可选全集 = useSysConfig 真数据 enabled 语言 code（组件默认 fallback 仅 zh/en，不读 mock seed）
  const enabledLangs = sysConfig.languages.filter((l) => l.enabled).map((l) => l.language_code);

  const queryClient = useQueryClient();
  const grantMutation = useMutation({
    mutationFn: () => {
      // 仅提交非空文案（契约 record 值 min(1)，空串会被服务端拒绝）
      const reason_i18n = Object.fromEntries(
        Object.entries(reasonI18n)
          .map(([k, v]) => [k, v.trim()] as const)
          .filter(([, v]) => v.length > 0),
      );
      return financeApi.grantBonus({
        user_id: u.id,
        amount: String(amount),
        reason_i18n,
      });
    },
    onSuccess: (dto) => {
      // toast 用服务端返回真值（实际入账数额 + 有效期）
      toast(
        `已向 ${u.name} 发放 ${Number(dto.amount).toLocaleString()} BONUS · 有效期至 ${fmtDate(dto.expires_at)}`,
        { tone: 'pos', icon: 'IconCoin' },
      );
      queryClient.invalidateQueries({ queryKey: ['users'] });
      onClose();
    },
    onError: (err) => {
      toast(`${u.name} · ${err instanceof Error ? err.message : '发放失败'}`, {
        tone: 'neg',
        icon: 'IconAlert',
      });
    },
  });

  const canSubmit =
    !!user &&
    amount > 0 &&
    amount <= 2000 && // 全局上限 2000（dev-docs A1 §9 / #167）；越界 server 返 FINANCE_BONUS_AMOUNT_INVALID
    // 必填 locale（en）有文案即可——与 MultiLangFormRows 标记的必填行一致
    requiredLangs.every((lang) => (reasonI18n[lang] ?? '').trim().length > 0) &&
    !grantMutation.isPending;

  return (
    <Dialog
      width={480}
      title={`发放 BONUS · ${u.name}`}
      subtitle={`${u.id} · 当前余额 ${u.bonus?.toLocaleString?.() ?? 0} BONUS`}
      icon="IconCoin"
      onClose={onClose}
      footer={
        <>
          <span style={{ flex: 1, fontSize: 11, color: 'var(--fg-muted)' }}>
            发放将立即到账 · 有效期 30 天 · 操作记入审计日志
          </span>
          <button className="btn" onClick={onClose}>
            取消
          </button>
          <button
            className="btn btn-accent"
            disabled={!canSubmit}
            onClick={() => grantMutation.mutate()}
          >
            <Icons.IconCoin size={11} />
            确认发放
          </button>
        </>
      }
    >
      <div style={{ padding: 18 }}>
        <FormRow label="数额" required hint="单次最多发放 2,000 BONUS">
          <div className="vstack" style={{ gap: 8 }}>
            <div className="hstack" style={{ gap: 6 }}>
              <input
                type="number"
                className="input mono"
                style={{ width: 140, fontSize: 13 }}
                value={amount}
                min={1}
                max={2000}
                onChange={(e) =>
                  setAmount(Math.max(0, Math.min(2000, Number(e.target.value) || 0)))
                }
              />
              <span className="mono muted" style={{ fontSize: 11 }}>
                BONUS
              </span>
            </div>
            <div className="hstack" style={{ gap: 4, flexWrap: 'wrap' }}>
              {PRESETS.map((p) => (
                <button
                  key={p}
                  className="btn btn-sm"
                  onClick={() => setAmount(p)}
                  style={{
                    fontSize: 11,
                    background: amount === p ? 'var(--accent-soft)' : undefined,
                    color: amount === p ? 'var(--accent)' : undefined,
                    borderColor: amount === p ? 'var(--accent)' : undefined,
                  }}
                >
                  +{p.toLocaleString()}
                </button>
              ))}
            </div>
          </div>
        </FormRow>
        <FormRow label="快捷原因" hint="点击预填中文文案">
          <div className="hstack" style={{ gap: 4, flexWrap: 'wrap' }}>
            {REASONS.map((r) => (
              <button
                key={r}
                className="btn btn-sm"
                onClick={() => setReasonI18n((prev) => ({ ...prev, zh: r }))}
                style={{
                  fontSize: 11,
                  background: reasonI18n.zh === r ? 'var(--accent-soft)' : undefined,
                  color: reasonI18n.zh === r ? 'var(--accent)' : undefined,
                  borderColor: reasonI18n.zh === r ? 'var(--accent)' : undefined,
                }}
              >
                {r}
              </button>
            ))}
          </div>
        </FormRow>
        {/* 发放原因给 C 端展示 → 契约 reason_i18n 多语言录入（必填 zh/en，en 为契约强校验地板） */}
        <MultiLangFormRows
          label="发放原因"
          value={reasonI18n}
          onChange={setReasonI18n}
          requiredLangs={requiredLangs}
          pinnedLangs={pinnedLangs}
          availableLangs={enabledLangs}
        />
        <FormRow label="备注" hint="契约无备注字段 · 暂不可用">
          <input
            className="input"
            placeholder="补充说明，如工单号、活动 ID 等"
            disabled
            title={NOT_IN_SCOPE_TITLE}
            style={{ fontSize: 12, background: 'var(--bg-subtle)', cursor: 'not-allowed' }}
          />
        </FormRow>
      </div>
    </Dialog>
  );
}
